import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import './Onboarding.css';

const STEPS = [
  {
    emoji: '🪶',
    title: 'Bienvenue sur Plumly !',
    text: 'La plateforme de lecture sociale qui révolutionne la découverte littéraire.',
  },
  {
    emoji: '👆',
    title: 'Swipe pour découvrir',
    text: 'Swipe à droite pour ajouter un livre à ta bibliothèque, à gauche pour passer. C\'est aussi simple que ça !',
  },
  {
    emoji: '📖',
    title: 'Lis et personnalise',
    text: '6 thèmes de lecture, mode zen, signets, et un éditeur de texte riche. La lecture comme tu l\'aimes.',
  },
  {
    emoji: '🤝',
    title: 'Rejoins la communauté',
    text: 'Suis des auteurs, commente, réagis, envoie des messages. Plumly c\'est avant tout une communauté.',
  },
  {
    emoji: '✍️',
    title: 'Écris tes propres histoires',
    text: 'Tu as une histoire à raconter ? Publie-la et partage-la avec le monde.',
  },
];

export default function Onboarding({ onComplete }) {
  const { user } = useUser();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Vérifier Firestore d'abord, localStorage en fallback
    if (user.onboardingDone) return;
    const seen = localStorage.getItem('plumly_onboarding_done');
    if (!seen) setVisible(true);
  }, [user]);

  const markDone = async () => {
    localStorage.setItem('plumly_onboarding_done', 'true');
    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.id), { onboardingDone: true });
      } catch (e) {
        console.warn('Erreur sauvegarde onboarding:', e);
      }
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      markDone();
      setVisible(false);
      if (onComplete) onComplete();
    }
  };

  const handleSkip = () => {
    markDone();
    setVisible(false);
    if (onComplete) onComplete();
  };

  if (!visible) return null;

  const current = STEPS[step];

  return (
    <div className="onboarding-overlay">
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          className="onboarding-card"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="onboarding-emoji"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            {current.emoji}
          </motion.div>
          <h2 className="onboarding-title">{current.title}</h2>
          <p className="onboarding-text">{current.text}</p>

          <div className="onboarding-dots">
            {STEPS.map((_, i) => (
              <div key={i} className={'onboarding-dot ' + (i === step ? 'onboarding-dot-active' : '')} />
            ))}
          </div>

          <div className="onboarding-actions">
            <motion.button
              className="btn btn-primary onboarding-next"
              onClick={handleNext}
              whileTap={{ scale: 0.95 }}
            >
              {step === STEPS.length - 1 ? 'C\'est parti ! 🚀' : 'Suivant'}
            </motion.button>
            {step < STEPS.length - 1 && (
              <button className="onboarding-skip" onClick={handleSkip}>
                Passer le tutoriel
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}